import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PracticeMode from './components/PracticeMode';
import SessionStats from './components/SessionStats';
import AudioPlayer from './components/AudioPlayer';
import ModeSelector from './components/ModeSelector';
import PracticeResults from './components/PracticeResults';
import ProgressiveChunking from './components/ProgressiveChunking';
import VerseRangeSelector from './components/VerseRangeSelector';
import WordOrderMode from './components/WordOrderMode';
import ProgressiveRevealMode from './components/ProgressiveRevealMode';
import { verseService } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import { trackVersePractice } from '../../services/streakService';
import { trackPracticeSessionStarted, trackPracticeSessionCompleted } from '../../utils/ga';
import { checkAndGrantAwards } from '../../services/awardService';

import { FadeSlideIn } from '../../components/ui/SkeletonLoader';

const MemorizationPractice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentVerseIndex, setCurrentVerseIndex] = useState(0);
  const [practiceMode, setPracticeMode] = useState('chunking');
  const [sessionStats, setSessionStats] = useState({
    accuracy: 0,
    timeElapsed: 0,
    streak: 7,
    points: 0,
    versesCompleted: 0,
    totalVerses: 0,
    earnedAwards: []
  });
  const [showResults, setShowResults] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [isSetupMode, setIsSetupMode] = useState(true);
  const [spacedRepetitionMode, setSpacedRepetitionMode] = useState(false);
  const [reviewResults, setReviewResults] = useState([]);
  
  const [error, setError] = useState(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [currentMode, setCurrentMode] = useState('chunking');

  // ── Synchronously initialize verses + noVerseMode from location.state ──────
  // This avoids the async useEffect race where noVerseMode flashes true before
  // the effect resolves location.state.
  const [verses, setVerses] = useState(() => {
    const incomingPhase0 = location?.state?.phase0Verses;
    const isPhase0 = location?.state?.isPhase0Session;
    if (isPhase0 && incomingPhase0?.length > 0) return incomingPhase0;
    return [];
  });
  const [noVerseMode, setNoVerseMode] = useState(() => {
    const selectedVerse = location?.state?.selectedVerse;
    const incomingPhase0 = location?.state?.phase0Verses;
    const isPhase0 = location?.state?.isPhase0Session;
    // Not no-verse if: a verse from reader was passed, OR phase0 session with verses
    if (selectedVerse) return false;
    if (isPhase0 && incomingPhase0?.length > 0) return false;
    return true;
  });
  const [showRangeSelector, setShowRangeSelector] = useState(() => {
    return !!(location?.state?.selectedVerse);
  });
  const [baseVerseFromReader, setBaseVerseFromReader] = useState(() => {
    return location?.state?.selectedVerse || null;
  });

  const [difficulty, setDifficulty] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);

  const [phase0Verses, setPhase0Verses] = useState([]);
  const [phase0Loading, setPhase0Loading] = useState(false);
  const [selectedPhase0Ids, setSelectedPhase0Ids] = useState([]);
  
  // Sequential mode state (for Phase 0 autoplay from review sessions)
  const [isSequentialMode, setIsSequentialMode] = useState(false);
  const [sequentialAutoplay, setSequentialAutoplay] = useState(false);

  // Text size state — shared with Bible Reader via localStorage
  const TEXT_SIZES = [
    { key: 'sm', label: 'S', px: '11px' },
    { key: 'base', label: 'M', px: '13px' },
    { key: 'lg', label: 'L', px: '16px' },
    { key: 'xl', label: 'XL', px: '20px' },
  ];
  const [textSize, setTextSize] = useState(() => localStorage.getItem('bibleReaderTextSize') || 'base');
  const getTextSizePx = () => TEXT_SIZES?.find(s => s?.key === textSize)?.px || '13px';
  const handleTextSizeChange = (key) => {
    setTextSize(key);
    localStorage.setItem('bibleReaderTextSize', key);
  };

  // Mock verses for practice - limited to memorization bucket verses only
  const practiceVerses = [];
  const currentVerse = verses?.[currentVerseIndex];

  // ── Initialize from location state (verse passed from Bible Reader) ────────
  useEffect(() => {
    const initFromState = async () => {
      // Get current user
      const { data: sessionData } = await supabase?.auth?.getSession();
      const currentUser = sessionData?.session?.user || null;
      setUser(currentUser);
      setUserId(currentUser?.id || null);

      // noVerseMode / verses / showRangeSelector are already initialized synchronously
      // above via useState lazy initializers. We only need to load phase0 verses from
      // DB here if we're in noVerseMode (no incoming state).
      const isPhase0Session = location?.state?.isPhase0Session;
      const incomingPhase0Verses = location?.state?.phase0Verses;
      const selectedVerse = location?.state?.selectedVerse;

      const isNoVerse = !selectedVerse && !(isPhase0Session && incomingPhase0Verses?.length > 0);

      if (isNoVerse && currentUser?.id) {
        setPhase0Loading(true);
        try {
          const { data } = await supabase
            ?.from('verses')
            ?.select('id, reference, text, version, current_phase')
            ?.eq('user_id', currentUser?.id)
            ?.eq('current_phase', 0)
            ?.order('reference', { ascending: true });
          setPhase0Verses(data || []);
        } catch (err) {
          console.warn('Failed to load phase 0 verses:', err);
        } finally {
          setPhase0Loading(false);
        }
      }
    };

    initFromState();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!isSetupMode && !showResults) {
        setSessionStats(prev => ({
          ...prev,
          timeElapsed: Math.floor((Date.now() - sessionStartTime) / 1000)
        }));
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [sessionStartTime, isSetupMode, showResults]);

  useEffect(() => {
    const versesToUse = verses?.length > 0 ? verses : practiceVerses;
    setSessionStats(prev => ({
      ...prev,
      totalVerses: versesToUse?.length
    }));
  }, [verses]);

  const handleStartPractice = () => {
    // If chunking mode is selected, go directly to chunking practice
    if (practiceMode === 'chunking') {
      setSessionActive(true);
      setIsSetupMode(false);
    } else {
      setSessionActive(true);
      setIsSetupMode(false);
    }
    setSessionStats(prev => ({
      ...prev,
      timeElapsed: 0
    }));
    trackPracticeSessionStarted(practiceMode, verses?.length || 0);
  };

  /**
   * Called when user confirms a verse range from VerseRangeSelector.
   * Builds a single combined verse object and starts the practice session.
   */
  const handleRangeConfirm = ({ verses: selectedVerses, reference, combinedText, book, chapter, verseStart, verseEnd, version: ver }) => {
    const combinedVerse = {
      id: Date.now(),
      reference,
      text: combinedText,
      book,
      chapter,
      verse: verseStart,
      verseEnd,
      version: ver || 'KJV',
      bucket: 'Daily',
      inLibrary: baseVerseFromReader?.inLibrary || false,
    };
    setVerses([combinedVerse]);
    setShowRangeSelector(false);
    // Go straight to setup (mode selection) — don't auto-start
  };

  /**
   * Called when user skips range selection — use the single base verse.
   */
  const handleRangeSkip = () => {
    if (baseVerseFromReader) {
      setVerses([baseVerseFromReader]);
    }
    setShowRangeSelector(false);
  };

  const handleStartSpacedRepetitionReview = () => {
    setSpacedRepetitionMode(true);
    setSessionActive(true);
    setLoading(true);
    // Logic to load spaced repetition verses
    setLoading(false);
  };

  const calculateAccuracy = (userAnswer, correctAnswer) => {
    const userWords = userAnswer?.toLowerCase()?.split(/\s+/);
    const correctWords = correctAnswer?.toLowerCase()?.split(/\s+/);
    
    let matches = 0;
    const maxLength = Math.max(userWords?.length, correctWords?.length);
    
    for (let i = 0; i < maxLength; i++) {
      if (userWords?.[i] === correctWords?.[i]) {
        matches++;
      }
    }
    
    return Math.round((matches / correctWords?.length) * 100);
  };

  const calculatePoints = (accuracy) => {
    // Simplified point calculation without difficulty multiplier
    return Math.round(accuracy);
  };

  const handlePracticeComplete = async (results) => {
    try {
      setResults(results);
      setShowResults(true);
      trackPracticeSessionCompleted(
        practiceMode,
        results?.accuracy || 0,
        results?.totalVerses || verses?.length || 0
      );

      // Track streak activity when practice is completed successfully
      if (user?.id) {
        const completedCount = results?.versesCompleted || sessionStats?.versesCompleted || verses?.length || 1;
        await trackVersePractice(user?.id, completedCount);
      }

      // Grant awards for session completion
      if (user?.id) {
        try {
          const { count: totalSessions } = await supabase
            ?.from('user_review_sessions')
            ?.select('id', { count: 'exact', head: true })
            ?.eq('user_id', user?.id)
            ?.eq('is_complete', true);
          await checkAndGrantAwards(user?.id, 'SESSION_COMPLETED', {
            totalSessions: (totalSessions || 0) + 1,
            accuracy: results?.accuracy || 0,
          });
          await checkAndGrantAwards(user?.id, 'SESSION_MILESTONE', {
            totalSessions: (totalSessions || 0) + 1,
          });
        } catch (awardErr) {
          console.warn('Award grant error:', awardErr);
        }
      }

      // If using spaced repetition mode, update each verse's spaced repetition data
      if (spacedRepetitionMode && results?.verseResults) {
        const spacedRepetitionUpdates = [];
        
        for (const result of results?.verseResults) {
          if (result?.verseId) {
            const updateResult = await verseService?.updateVerseSpacedRepetition(
              result?.verseId, 
              result?.success || false
            );
            
            if (updateResult?.error) {
              console.error('Failed to update spaced repetition:', updateResult?.error);
            } else {
              spacedRepetitionUpdates?.push({
                verseId: result?.verseId,
                ...updateResult?.data
              });
            }
          }
        }

        setReviewResults(spacedRepetitionUpdates);
        
        // Show enhanced results with phase information
        setResults({
          ...results,
          spacedRepetitionUpdates,
          message: `Practice completed! ${spacedRepetitionUpdates?.filter(u => u?.phase_advanced)?.length} verses advanced phases.`
        });
      }

    } catch (error) {
      console.error('Practice completion error:', error);
      setError(error?.message || 'Failed to complete practice session');
    }
  };

  const handleHint = () => {
    // Hint logic would be implemented here
    console.log('Hint requested');
  };

  const handleContinuePractice = () => {
    setShowResults(false);
    setCurrentVerseIndex(0);
    setSessionStats(prev => ({
      ...prev,
      versesCompleted: 0,
      accuracy: 0,
      points: 0,
      timeElapsed: 0
    }));
  };

  const handleRestartSession = () => {
    setShowResults(false);
    setIsSetupMode(true);
    setCurrentVerseIndex(0);
    setSessionStats({
      accuracy: 0,
      timeElapsed: 0,
      streak: 7,
      points: 0,
      versesCompleted: 0,
      totalVerses: practiceVerses?.length
    });
  };

  const handleExitPractice = () => {
    navigate('/dashboard');
  };

  const handleNextVerse = () => {
    if (currentVerseIndex < verses?.length - 1) {
      setCurrentVerseIndex(currentVerseIndex + 1);
    } else {
      // All verses completed in sequential mode
      handleSequentialComplete();
    }
  };

  const handleSequentialComplete = () => {
    // Show completion message and navigate back
    setShowResults(true);
    setResults({
      totalVerses: verses?.length,
      correctCount: verses?.length,
      accuracy: 100,
      message: 'Sequential practice session completed!'
    });
    
    // Navigate back to review system after delay
    setTimeout(() => {
      navigate('/daily-review-system');
    }, 3000);
  };

  const handleStartWithPhase0 = () => {
    const selectedVerses = phase0Verses?.filter(v => selectedPhase0Ids?.includes(v?.id));
    setVerses(selectedVerses);
    setNoVerseMode(false);
  };

  // ── No Verse Screen ───────────────────────────────────────────────────────
  if (noVerseMode) {
    const allSelected = selectedPhase0Ids?.length === phase0Verses?.length && phase0Verses?.length > 0;

    const toggleAll = () => {
      if (allSelected) setSelectedPhase0Ids([]);
      else setSelectedPhase0Ids(phase0Verses?.map(v => v?.id));
    };

    const toggleVerse = (id) => {
      setSelectedPhase0Ids(prev =>
        prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id]
      );
    };

    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8 pb-20 md:pb-8">
          <div className="max-w-lg mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Icon name="BookOpen" size={28} className="text-primary" />
              </div>
              <h1 className="text-2xl font-heading font-semibold text-foreground mb-2">
                No Verse Selected
              </h1>
              <p className="text-sm text-muted-foreground text-relaxed">
                To start practicing, select a verse from the Bible Reader or choose from your Phase 0 verses below.
              </p>
            </div>

            {/* Option 1: Go to Bible Reader */}
            <button
              onClick={() => navigate('/bible-reader')}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-primary/30 bg-primary/5 hover:bg-primary/10 hover:border-primary/50 transition-all duration-200 mb-4 text-left group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/25 transition-colors">
                <Icon name="BookMarked" size={20} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground text-sm">Select from Bible Reader</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Open a verse in the Bible Reader and tap the practice action
                </p>
              </div>
              <Icon name="ArrowRight" size={16} className="text-primary/60 flex-shrink-0" />
            </button>

            {/* Option 2: Phase 0 verses */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="px-4 py-3 border-b border-border bg-muted/20 flex items-center gap-2">
                <Icon name="Layers" size={16} className="text-primary" />
                <span className="text-sm font-semibold text-foreground">Your Phase 0 Verses</span>
                {!phase0Loading && phase0Verses?.length > 0 && (
                  <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                    {phase0Verses?.length} verse{phase0Verses?.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {phase0Loading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground text-sm">
                  <Icon name="Loader" size={16} className="animate-spin" />
                  Loading your verses…
                </div>
              ) : !userId ? (
                <div className="px-4 py-6 text-center">
                  <p className="text-sm text-muted-foreground mb-3">Sign in to see your Phase 0 verses</p>
                  <Button variant="default" size="sm" onClick={() => navigate('/login')}>
                    Sign In
                  </Button>
                </div>
              ) : phase0Verses?.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <Icon name="Inbox" size={24} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No Phase 0 verses in your library yet.</p>
                  <p className="text-xs text-muted-foreground mt-1">Add verses from the Bible Reader to get started.</p>
                </div>
              ) : (
                <>
                  {/* Select all row */}
                  <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/10">
                    <button
                      onClick={toggleAll}
                      className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Icon name={allSelected ? 'CheckSquare' : 'Square'} size={14} className={allSelected ? 'text-primary' : ''} />
                      {allSelected ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedPhase0Ids?.length > 0 && (
                      <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                        {selectedPhase0Ids?.length} selected
                      </span>
                    )}
                  </div>

                  {/* Verse list */}
                  <div className="divide-y divide-border max-h-64 overflow-y-auto">
                    {phase0Verses?.map(v => (
                      <button
                        key={v?.id}
                        onClick={() => toggleVerse(v?.id)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/20 transition-colors text-left"
                      >
                        <Icon
                          name={selectedPhase0Ids?.includes(v?.id) ? 'CheckSquare' : 'Square'}
                          size={16}
                          className={selectedPhase0Ids?.includes(v?.id) ? 'text-primary flex-shrink-0' : 'text-muted-foreground flex-shrink-0'}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{v?.reference}</p>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{v?.text?.slice(0, 60)}{v?.text?.length > 60 ? '…' : ''}</p>
                        </div>
                        <span className="text-xs text-muted-foreground flex-shrink-0">{v?.version}</span>
                      </button>
                    ))}
                  </div>

                  {/* Start button */}
                  <div className="px-4 py-3 border-t border-border">
                    <Button
                      variant="default"
                      fullWidth
                      onClick={handleStartWithPhase0}
                      disabled={selectedPhase0Ids?.length === 0}
                      iconName="Play"
                      iconPosition="left"
                    >
                      Start Memorizing ({selectedPhase0Ids?.length} verse{selectedPhase0Ids?.length !== 1 ? 's' : ''})
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ── Range Selector Screen ─────────────────────────────────────────────────
  if (showRangeSelector && baseVerseFromReader) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8 pb-20 md:pb-8">
          <div className="max-w-lg mx-auto">
            {/* Back button */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to Bible Reader
            </button>

            <div className="text-center mb-6">
              <h1 className="text-2xl font-heading font-semibold text-foreground mb-1">
                Memorization Practice
              </h1>
              <p className="text-sm text-muted-foreground">
                Customize the verse range you'd like to memorize
              </p>
            </div>

            <VerseRangeSelector
              baseVerse={baseVerseFromReader}
              onConfirm={handleRangeConfirm}
              onSkip={handleRangeSkip}
            />
          </div>
        </main>
      </div>
    );
  }

  if (showResults) {
    const results = {
      accuracy: sessionStats?.accuracy,
      timeElapsed: sessionStats?.timeElapsed,
      pointsEarned: sessionStats?.points,
      versesCompleted: sessionStats?.versesCompleted,
      perfectVerses: sessionStats?.versesCompleted || 0,
      hintsUsed: 0,
      newAchievements: sessionStats?.earnedAwards?.length > 0
        ? sessionStats?.earnedAwards?.map(a => a?.name || a)
        : []
    };

    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8 pb-20 md:pb-8">
          <div className="max-w-2xl mx-auto">
            <PracticeResults
              results={results}
              onContinue={handleContinuePractice}
              onRestart={handleRestartSession}
              onExit={handleExitPractice}
              onNewSession={handleRestartSession}
            />
          </div>
        </main>
      </div>
    );
  }

  if (isSetupMode) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8 pb-20 md:pb-8">
          <FadeSlideIn>
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-heading font-semibold text-foreground mb-2">
                Memorization Practice
              </h1>
              <p className="text-lg text-muted-foreground">
                Interactive exercises to strengthen your scripture memorization from your current verses
              </p>
            </div>

            <div className="grid lg:grid-cols-1 gap-6 justify-center">
              {/* Mode Selection */}
              <div className="max-w-md mx-auto">
                <ModeSelector
                  currentMode={practiceMode}
                  onModeChange={setPracticeMode}
                />
              </div>
            </div>

            {/* Start Button */}
            <div className="mt-8 text-center">
              <Button
                variant="default"
                size="lg"
                onClick={handleStartPractice}
                iconName="Play"
                iconPosition="left"
                className="px-8"
              >
                Start Practice Session
              </Button>
            </div>
          </div>
          </FadeSlideIn>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 pb-20 md:pb-6">
        <div className="animate-fade-slide-up" style={{ animationFillMode: 'both' }}>
        {/* Error Display */}
        {error && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <p className="text-destructive-foreground">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!sessionActive ? (
          <div className="max-w-4xl mx-auto">
            {/* Page Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-heading font-semibold text-foreground mb-2">
                Memorization Practice
              </h1>
              <p className="text-lg text-muted-foreground">
                Interactive exercises to strengthen your scripture memorization from your current verses
              </p>
            </div>

            <div className="grid lg:grid-cols-1 gap-6 justify-center">
              {/* Mode Selection */}
              <div className="max-w-md mx-auto">
                <ModeSelector
                  currentMode={practiceMode}
                  onModeChange={setPracticeMode}
                />
              </div>
            </div>

            {/* Start Button */}
            <div className="mt-8 text-center">
              <Button
                variant="default"
                size="lg"
                onClick={handleStartPractice}
                iconName="Play"
                iconPosition="left"
                className="px-8"
              >
                Start Practice Session
              </Button>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            {/* Practice Area */}
            {practiceMode === 'chunking' ? (
              <div className="space-y-6">
                {/* Simple Header for Chunking */}
                <div className="flex items-center gap-4 mb-6">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSessionActive(false);
                      setIsSetupMode(true);
                    }}
                    className="flex-shrink-0"
                  >
                    <Icon name="ArrowLeft" size={20} />
                  </Button>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-foreground">
                      Progressive Chunking Practice
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Build the verse progressively with repetition
                    </p>
                  </div>
                  {/* Text Size Control */}
                  <div className="flex items-center gap-0 border border-border rounded-md overflow-hidden bg-background flex-shrink-0">
                    <button
                      onClick={() => {
                        const idx = TEXT_SIZES?.findIndex(s => s?.key === textSize);
                        if (idx > 0) handleTextSizeChange(TEXT_SIZES?.[idx - 1]?.key);
                      }}
                      title="Decrease text size"
                      disabled={TEXT_SIZES?.findIndex(s => s?.key === textSize) === 0}
                      className="px-2.5 py-1.5 font-medium transition-colors min-w-[28px] min-h-[32px] leading-none text-sm text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      −
                    </button>
                    <span
                      className="px-2 py-1.5 min-w-[28px] min-h-[32px] leading-none flex items-center justify-center font-medium text-foreground select-none border-x border-border"
                      style={{ fontSize: getTextSizePx() }}
                    >
                      T
                    </span>
                    <button
                      onClick={() => {
                        const idx = TEXT_SIZES?.findIndex(s => s?.key === textSize);
                        if (idx < TEXT_SIZES?.length - 1) handleTextSizeChange(TEXT_SIZES?.[idx + 1]?.key);
                      }}
                      title="Increase text size"
                      disabled={TEXT_SIZES?.findIndex(s => s?.key === textSize) === TEXT_SIZES?.length - 1}
                      className="px-2.5 py-1.5 font-medium transition-colors min-w-[28px] min-h-[32px] leading-none text-sm text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Practice Session - Chunking Mode */}
                {sessionActive && practiceMode === 'chunking' && currentVerse && (
                  <div className="max-w-4xl mx-auto">
                    {/* Sequential Mode Header */}
                    {isSequentialMode && (
                      <div className="mb-6 p-4 bg-primary/10 border border-primary/30 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon name="List" size={20} className="text-primary" />
                            <span className="font-semibold text-foreground">
                              Sequential Practice Mode
                            </span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Verse {currentVerseIndex + 1} of {verses?.length}
                          </div>
                        </div>
                        {sequentialAutoplay && (
                          <p className="text-sm text-primary mt-2">
                            Autoplay enabled - verses will play automatically
                          </p>
                        )}
                      </div>
                    )}
                    
                    <ProgressiveChunking
                      verse={currentVerse}
                      onComplete={() => {
                        if (isSequentialMode) {
                          handleNextVerse();
                        } else {
                          setShowResults(true);
                        }
                      }}
                      verseQueue={verses}
                      currentVerseIndex={currentVerseIndex}
                      onNextVerse={handleNextVerse}
                      userId={userId}
                      skipSettings={isSequentialMode}
                      textSizePx={getTextSizePx()}
                    />
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Word Order Mode */}
                {practiceMode === 'word-order' && (
                  <WordOrderMode
                    verse={currentVerse}
                    verseIndex={currentVerseIndex}
                    totalVerses={verses?.length}
                    textSizePx={getTextSizePx()}
                    onComplete={async (result) => {
                      const newCompleted = (sessionStats?.versesCompleted || 0) + 1;
                      setSessionStats(prev => ({
                        ...prev,
                        versesCompleted: newCompleted,
                        accuracy: result?.correct ? Math.min(100, prev?.accuracy + Math.round(100 / (verses?.length || 1))) : prev?.accuracy,
                        points: prev?.points + (result?.correct ? 10 : 3),
                      }));
                      // Track streak on last verse completion
                      if (user?.id && newCompleted >= (verses?.length || 1)) {
                        await trackVersePractice(user?.id, newCompleted);
                      }
                    }}
                    onNext={() => {
                      if (currentVerseIndex < verses?.length - 1) {
                        setCurrentVerseIndex(prev => prev + 1);
                      } else {
                        setShowResults(true);
                      }
                    }}
                  />
                )}

                {/* Progressive Reveal Mode */}
                {practiceMode === 'progressive' && (
                  <ProgressiveRevealMode
                    verse={currentVerse}
                    verseIndex={currentVerseIndex}
                    totalVerses={verses?.length}
                    textSizePx={getTextSizePx()}
                    onComplete={async (result) => {
                      const newVersionsCompleted = (sessionStats?.versesCompleted || 0) + 1;
                      const newAccuracy = Math.min(100, (sessionStats?.accuracy || 0) + Math.round(100 / (verses?.length || 1)));

                      setSessionStats(prev => ({
                        ...prev,
                        versesCompleted: newVersionsCompleted,
                        accuracy: newAccuracy,
                        points: prev?.points + 10,
                      }));
                    }}
                    onNext={() => {
                      if (currentVerseIndex < verses?.length - 1) {
                        setCurrentVerseIndex(prev => prev + 1);
                      } else {
                        setShowResults(true);
                      }
                    }}
                  />
                )}

                {/* Fill-blank / other modes — legacy PracticeMode */}
                {practiceMode !== 'word-order' && practiceMode !== 'progressive' && (
                  <>
                {/* Enhanced Session Header */}
                <div className="bg-card border border-border rounded-lg p-4 mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSessionActive(false);
                          setSpacedRepetitionMode(false);
                          setReviewResults([]);
                        }}
                        className="flex-shrink-0"
                      >
                        <Icon name="ArrowLeft" size={20} />
                      </Button>
                      
                      <div>
                        <h2 className="text-xl font-semibold text-card-foreground">
                          {spacedRepetitionMode ? 'Spaced Repetition Review' : `${currentMode?.charAt(0)?.toUpperCase() + currentMode?.slice(1)} Practice`}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                          {spacedRepetitionMode 
                            ? `Reviewing ${verses?.length} verses due today`
                            : `${verses?.length} verses • ${difficulty?.charAt(0)?.toUpperCase() + difficulty?.slice(1)} difficulty`
                          }
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {spacedRepetitionMode && (
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium text-foreground">{verses?.filter(v => v?.isOverdue)?.length}</span> overdue
                        </div>
                      )}
                      
                      <div className="text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">{currentVerseIndex + 1}</span> of {verses?.length}
                      </div>
                      
                      <div className="w-24 bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${((currentVerseIndex + 1) / verses?.length) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Practice Modes Display */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-card rounded-lg p-4">
                    <h3 className="text-lg font-medium text-card-foreground mb-4">
                      {spacedRepetitionMode ? 'Review Status' : 'Practice Mode'}
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-muted rounded">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">
                            {spacedRepetitionMode ? 'Review Status' : 'Practice Mode'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {spacedRepetitionMode ? '7-Phase Algorithm' : 'Fill Blank'}
                          </p>
                        </div>
                        <div className={`w-2 h-2 rounded-full ${
                          spacedRepetitionMode ? 'bg-primary' : 'bg-success'
                        }`} />
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-muted rounded">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">
                            Difficulty
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {difficulty?.charAt(0)?.toUpperCase() + difficulty?.slice(1)}
                          </p>
                        </div>
                        <div className={`w-2 h-2 rounded-full ${
                          difficulty === 'easy' ? 'bg-success' : 
                          difficulty === 'medium' ? 'bg-warning' : 'bg-destructive'
                        }`} />
                      </div>
                    </div>
                  </div>

                  <div className="bg-card rounded-lg p-4">
                    <h3 className="text-lg font-medium text-card-foreground mb-4">
                      {spacedRepetitionMode ? 'Review Progress' : 'Practice Progress'}
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-muted rounded">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">
                            {spacedRepetitionMode ? 'Review Status' : 'Practice Mode'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {spacedRepetitionMode ? '7-Phase Algorithm' : 'Fill Blank'}
                          </p>
                        </div>
                        <div className={`w-2 h-2 rounded-full ${
                          spacedRepetitionMode ? 'bg-primary' : 'bg-success'
                        }`} />
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-muted rounded">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">
                            Difficulty
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {difficulty?.charAt(0)?.toUpperCase() + difficulty?.slice(1)}
                          </p>
                        </div>
                        <div className={`w-2 h-2 rounded-full ${
                          difficulty === 'easy' ? 'bg-success' : 
                          difficulty === 'medium' ? 'bg-warning' : 'bg-destructive'
                        }`} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Practice Area */}
                <div className="grid lg:grid-cols-3 gap-6">
                  {/* Main Practice Area */}
                  <div className="lg:col-span-2 space-y-6">
                    <PracticeMode
                      verse={currentVerse}
                      mode={practiceMode}
                      onComplete={handlePracticeComplete}
                      onHint={handleHint}
                    />

                    {/* Audio Player */}
                    <AudioPlayer verse={currentVerse} />
                  </div>

                  {/* Sidebar */}
                  <div className="space-y-6">
                    {/* Session Stats */}
                    <SessionStats
                      accuracy={sessionStats?.accuracy}
                      timeElapsed={sessionStats?.timeElapsed}
                      streak={sessionStats?.streak}
                      points={sessionStats?.points}
                      versesCompleted={sessionStats?.versesCompleted}
                      totalVerses={sessionStats?.totalVerses}
                    />

                    {/* Quick Actions */}
                    <div className="bg-card rounded-lg p-4 shadow-warm">
                      <h3 className="font-medium text-card-foreground mb-4">Quick Actions</h3>
                      <div className="space-y-3">
                        <Button
                          variant="outline"
                          fullWidth
                          onClick={() => {
                            if (currentVerseIndex < verses?.length - 1) {
                              setCurrentVerseIndex(currentVerseIndex + 1);
                            }
                          }}
                          disabled={currentVerseIndex >= verses?.length - 1}
                          iconName="SkipForward"
                          iconPosition="left"
                        >
                          Skip Verse
                        </Button>
                        
                        <Button
                          variant="ghost"
                          fullWidth
                          onClick={() => navigate('/verse-library')}
                          iconName="Library"
                          iconPosition="left"
                        >
                          View Library
                        </Button>
                        
                        <Button
                          variant="ghost"
                          fullWidth
                          onClick={handleExitPractice}
                          iconName="Home"
                          iconPosition="left"
                        >
                          Exit Practice
                        </Button>
                      </div>
                    </div>

                    {/* Motivational Quote */}
                    <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
                      <div className="flex items-start space-x-2">
                        <Icon name="Quote" size={16} color="var(--color-accent)" className="mt-1" />
                        <div>
                          <p className="text-sm text-accent-foreground italic">
                            "The heart of man plans his way, but the Lord establishes his steps."
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">- Proverbs 16:9</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enhanced Results Display with Spaced Repetition Feedback */}
                {results && (
                  <div className="max-w-2xl mx-auto">
                    <PracticeResults 
                      results={results} 
                      onNewSession={() => {
                        setResults(null);
                        setSessionActive(false);
                        setSpacedRepetitionMode(false);
                        setReviewResults([]);
                      }}
                      onContinue={() => {
                        setResults(null);
                        setCurrentVerseIndex(0);
                        setSessionStartTime(Date.now());
                      }}
                      spacedRepetitionMode={spacedRepetitionMode}
                      reviewResults={reviewResults}
                    />
                  </div>
                )}
                  </>
                )}
              </>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default MemorizationPractice;